import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Download, ExternalLink } from 'lucide-react';
import { buildGoogleCalendarUrl, triggerIcsDownload } from '../utils/calendar';

/**
 * "Add to Calendar" dropdown: ICS download (universal) + Google Calendar link.
 * No OAuth, no SDKs, works on iOS/Android/Desktop.
 */
const AddToCalendarButton = ({ event, business, variant = 'default' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // Use `pointerdown` instead of `mousedown` so touch + mouse + stylus all
    // trigger the outside-click close. On iOS Safari + Chrome Android, plain
    // `mousedown` listeners are unreliable for synthesizing close-on-tap.
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, []);

  if (!event?.id || !event.startDate) return null;

  const baseBtn =
    variant === 'small'
      ? 'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-white border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors'
      : 'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-white border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors';

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(v => !v); }}
        className={baseBtn}
        data-testid={`add-to-calendar-btn-${event.id}`}
        aria-expanded={open}
      >
        <CalendarPlus className={variant === 'small' ? 'h-3 w-3' : 'h-4 w-4'} />
        {t('calendar.addToCalendar', 'Add to Calendar')}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-lg ring-1 ring-gray-100 z-50 overflow-hidden"
          data-testid={`add-to-calendar-menu-${event.id}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); triggerIcsDownload(event.id); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50"
            data-testid={`calendar-ics-${event.id}`}
          >
            <Download className="h-4 w-4 text-gray-500 shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{t('calendar.icsDownload', 'Apple / Outlook (.ics)')}</p>
              <p className="text-[11px] text-gray-500">{t('calendar.icsHint', 'Universal calendar file')}</p>
            </div>
          </button>
          <a
            href={buildGoogleCalendarUrl(event, business || {})}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-t border-gray-100"
            data-testid={`calendar-google-${event.id}`}
          >
            <ExternalLink className="h-4 w-4 text-gray-500 shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{t('calendar.googleCalendar', 'Google Calendar')}</p>
              <p className="text-[11px] text-gray-500">{t('calendar.googleHint', 'Opens in a new tab')}</p>
            </div>
          </a>
        </div>
      )}
    </div>
  );
};

export default AddToCalendarButton;
